import {
  versionConflictLabel,
  versionConflictMissing,
  versionConflictToast,
  versionConflictUpdatedReload,
} from '@/i18n/versionConflictText';
import { sanitizeTemuFormImageSlots } from '@/temu-platform/utils/sanitizeTemuFormImageSlots';

/**
 * TEMU 素材模板乐观锁：版本采集 / 比对 / 冲突判定。
 */

export const TEMPLATE_VERSION_TYPES = Object.freeze({
  product: 'product',
  sizeChart: 'sizeChart',
  attribute: 'attribute',
  photo: 'photo',
});

export const TEMPLATE_VERSION_LABELS = Object.freeze({
  product: '产品模板',
  sizeChart: '尺码库',
  attribute: '属性库',
  photo: '实拍图库',
});

function displayVersionLabel(type) {
  return versionConflictLabel(type, TEMPLATE_VERSION_LABELS[type]);
}

/** 发品页字段标记挂载点 */
export const TEMPLATE_CONFLICT_FIELD_KEYS = Object.freeze({
  product: 'productTemplate',
  sizeChart: 'sizeChart',
  attribute: 'productAttributes',
  photo: 'photoTemplate',
});

export function parseTemplateVersion(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function isTemplateVersionConflictError(err) {
  const code = String(err?.code || '').toUpperCase();
  if (code === 'DATA_CONFLICT' || code === 'DATA_TOO_LONG' || code === 'DUPLICATE_TASK_ID') {
    return false;
  }
  const msg = String(err?.message || '');
  if (/data truncation|data too long/i.test(msg)) {
    return false;
  }
  if (msg.includes('已被更新') || msg.includes('请刷新后重试') || msg.includes('请重新加载对应区域')) {
    return true;
  }
  const status = Number(err?.status);
  return status === 409;
}

export function collectAppliedTemplateRefs({
  productTemplateId,
  productTemplateVersion,
  attrTemplateId,
  attrTemplateVersion,
  sizeChartTemplateId,
  sizeChartTemplateVersion,
  photoTemplateId,
  photoTemplateVersion,
} = {}) {
  const refs = [];
  const push = (type, id, version) => {
    const tid = String(id || '').trim();
    if (!tid) return;
    refs.push({
      type,
      id: tid,
      version: parseTemplateVersion(version),
    });
  };
  push(TEMPLATE_VERSION_TYPES.product, productTemplateId, productTemplateVersion);
  push(TEMPLATE_VERSION_TYPES.attribute, attrTemplateId, attrTemplateVersion);
  push(TEMPLATE_VERSION_TYPES.sizeChart, sizeChartTemplateId, sizeChartTemplateVersion);
  push(TEMPLATE_VERSION_TYPES.photo, photoTemplateId, photoTemplateVersion);
  return refs;
}

/**
 * @returns {Promise<{ ok: boolean, conflicts: Array }>}
 */
export async function checkTemplateVersions(api, shopId, refs = []) {
  const items = (Array.isArray(refs) ? refs : [])
    .filter((r) => r && r.type && r.id)
    .map((r) => ({
      type: r.type,
      id: Number(r.id) || r.id,
      version: parseTemplateVersion(r.version),
    }));
  if (!shopId || items.length === 0) {
    return { ok: true, conflicts: [] };
  }
  if (typeof api?.checkMaterialTemplateVersions === 'function') {
    const res = await api.checkMaterialTemplateVersions(shopId, { items });
    const conflicts = Array.isArray(res?.conflicts) ? res.conflicts : [];
    return { ok: res?.ok !== false && conflicts.length === 0, conflicts };
  }
  // 兜底：并行 GET 比对
  const conflicts = [];
  await Promise.all(items.map(async (item) => {
    try {
      const getter = resolveGetter(api, item.type);
      if (!getter) return;
      const res = await getter(shopId, item.id);
      const remote = res?.item || res || {};
      const actual = parseTemplateVersion(remote.version);
      if (item.version == null || actual == null || Number(item.version) !== Number(actual)) {
        conflicts.push({
          type: item.type,
          id: item.id,
          expectedVersion: item.version,
          actualVersion: actual,
          reason: 'version_mismatch',
          message: versionConflictUpdatedReload(displayVersionLabel(item.type)),
          name: remote.name,
        });
      }
    } catch (e) {
      conflicts.push({
        type: item.type,
        id: item.id,
        expectedVersion: item.version,
        actualVersion: null,
        reason: 'not_found',
        message: e?.message || versionConflictMissing(displayVersionLabel(item.type)),
      });
    }
  }));
  return { ok: conflicts.length === 0, conflicts };
}

function resolveGetter(api, type) {
  switch (type) {
    case TEMPLATE_VERSION_TYPES.product:
      return api.getProductTemplate?.bind(api);
    case TEMPLATE_VERSION_TYPES.sizeChart:
      return api.getSizeChartTemplate?.bind(api);
    case TEMPLATE_VERSION_TYPES.attribute:
      return api.getProductAttributeTemplate?.bind(api);
    case TEMPLATE_VERSION_TYPES.photo:
      return api.getPhotoTemplate?.bind(api);
    default:
      return null;
  }
}

export function conflictFieldKey(type) {
  return TEMPLATE_CONFLICT_FIELD_KEYS[type] || type;
}

export function isMissingTemplateError(err) {
  if (!err) return false;
  if (Number(err.status) === 404) return true;
  const msg = String(err.message || '');
  return /不存在|已删除|not found/i.test(msg);
}

export function isMissingLibraryRefConflict(conflict) {
  if (!conflict) return false;
  const type = String(conflict.type || '');
  if (type !== TEMPLATE_VERSION_TYPES.attribute && type !== TEMPLATE_VERSION_TYPES.sizeChart) {
    return false;
  }
  if (String(conflict.reason || '') === 'not_found') return true;
  return /不存在|已删除/i.test(String(conflict.message || ''));
}

export function partitionTemplateVersionConflicts(conflicts = []) {
  const list = Array.isArray(conflicts) ? conflicts : [];
  const missingLibraryRefs = [];
  const blocking = [];
  list.forEach((c) => {
    if (isMissingLibraryRefConflict(c)) missingLibraryRefs.push(c);
    else blocking.push(c);
  });
  return { missingLibraryRefs, blocking };
}

export function conflictToastMessage(conflicts = []) {
  return versionConflictToast(conflicts, displayVersionLabel);
}

/**
 * 将最新商品模板 merge 进现有 form（保留用户后改的非模板核心字段时：以 prev 为 base，覆盖模板定义键）。
 * 商品模板冲突重载用：不整表 emptyForm 重置。
 */
export function mergeProductTemplateIntoForm(prevForm, templateItem) {
  const prev = prevForm && typeof prevForm === 'object' ? prevForm : {};
  const item = templateItem && typeof templateItem === 'object' ? templateItem : {};
  const rawPayload = item.payload && typeof item.payload === 'object' ? item.payload : {};
  const formData = rawPayload.form && typeof rawPayload.form === 'object'
    ? rawPayload.form
    : rawPayload;
  const templateKeys = [
    'productAttributes',
    'sizeChart',
    'skcList',
    'selectedSizeIds',
    'sizeValueLabels',
    'productTypeId',
    'categoryId',
    'categoryPathLabel',
    'brandCode',
    'supplierCode',
    'title',
    'description',
    'mainImageList',
    'detailImageList',
    'printImages',
    'ipePrintImageNames',
    'selectedSites',
    'warehouseId',
  ];
  const next = { ...prev };
  templateKeys.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(formData, key)) {
      next[key] = formData[key];
    }
  });
  if (!next.categoryId && item.categoryId) next.categoryId = item.categoryId;
  if (!next.productTypeId && item.productTypeId) next.productTypeId = item.productTypeId;
  if (!next.categoryPathLabel && item.categoryPathLabel) {
    next.categoryPathLabel = item.categoryPathLabel;
  }
  return {
    form: sanitizeTemuFormImageSlots(next),
    selectedAttrTemplateId: String(
      rawPayload.selectedAttrTemplateId || formData.selectedAttrTemplateId || '',
    ).trim(),
    selectedSizeChartTemplateId: String(
      rawPayload.selectedSizeChartTemplateId || formData.selectedSizeChartTemplateId || '',
    ).trim(),
    version: parseTemplateVersion(item.version),
  };
}

export function extractAttrTemplatePatch(templateItem) {
  const item = templateItem && typeof templateItem === 'object' ? templateItem : {};
  const rawPayload = item.payload && typeof item.payload === 'object' ? item.payload : {};
  const attrs = rawPayload.productAttributes && typeof rawPayload.productAttributes === 'object'
    ? rawPayload.productAttributes
    : {};
  return {
    productAttributes: attrs,
    version: parseTemplateVersion(item.version),
  };
}

export function extractSizeChartTemplatePatch(templateItem) {
  const item = templateItem && typeof templateItem === 'object' ? templateItem : {};
  const rawPayload = item.payload && typeof item.payload === 'object' ? item.payload : {};
  const sizeChart = rawPayload.sizeChart && typeof rawPayload.sizeChart === 'object'
    ? rawPayload.sizeChart
    : (rawPayload && typeof rawPayload === 'object' && !rawPayload.kind ? rawPayload : {});
  return {
    sizeChart,
    officialSizeChart: rawPayload.officialSizeChart && typeof rawPayload.officialSizeChart === 'object'
      ? rawPayload.officialSizeChart
      : null,
    selectedSizeLabels: Array.isArray(rawPayload.selectedSizeLabels)
      ? rawPayload.selectedSizeLabels.map(String)
      : [],
    platformBusinessIds: Array.isArray(rawPayload.platformBusinessIds)
      ? rawPayload.platformBusinessIds.map(String)
      : [],
    kind: rawPayload.kind || '',
    version: parseTemplateVersion(item.version),
  };
}
